import { yupResolver } from '@hookform/resolvers/yup';
import { TicketBookingVenue, Venue, VenueType } from '@thurstech/commons/dist/entity/venue';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { Packages } from './Packages';
import { Slots } from './Slots';
import Button from '../../../components/button/Button';
import Input from '../../../components/input/Input';
import InputSelect from '../../../components/input/InputSelect';
import InputSlider from '../../../components/input/InputSlider';
import InputTextArea from '../../../components/input/InputTextArea';
import Loading from '../../../components/loading/Loading';
import { useAppDispatch, useAppSelector } from '../../../hooks/store';
import { errorMessage } from '../../../hooks/useNotifications';
import { isThunkLoading } from '../../../redux/common';
import {
  addVenueThunk,
  getListingThunk,
  getVenueThunk,
  updateVenueThunk
} from '../../../redux/reducer/listingSlice';
import { cityOptions, withErrorHandler } from '../../../utils/utils';
import {
  AddVenueFormData,
  addVenueSchema
} from '../../../validation/operations/addOrEditProduct/ticketBooking/addVenueSchema';

const AddVenue: React.FC = () => {
  const navigate = useNavigate();
  const { listingId, venueId } = useParams();

  const listing = useAppSelector(state => state.listing.listingMap[listingId ?? '']);
  const venue = useAppSelector(state => state.listing.venueMap[venueId ?? '']) as
    | TicketBookingVenue
    | undefined;
  const { isLoading, error } = useAppSelector(state => state.listing);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!listing) {
      dispatch(getListingThunk(listingId!));
    }
    if (venueId && !venue) {
      dispatch(getVenueThunk({ listingId: listingId!, venueId }));
    }
  }, [venueId]);

  let values: AddVenueFormData | undefined;
  if (venue) {
    values = venue as AddVenueFormData;
  }
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger
  } = useForm({
    resolver: yupResolver(addVenueSchema),
    values
  });

  if (isLoading[getListingThunk.pending.toString()] || isLoading[getVenueThunk.pending.toString()]) {
    return <Loading />;
  } else if (error[getListingThunk.pending.toString()] || error[getVenueThunk.pending.toString()]) {
    navigate('/not-found');
    errorMessage(error[getListingThunk.pending.toString()] ?? error[getVenueThunk.pending.toString()]);
    return <div />;
  }

  const onSave = (veneuData: AddVenueFormData) => {
    withErrorHandler(async () => {
      if (veneuData.isResourcesLinkedToVenue) {
        veneuData.packages.forEach(pkg => (pkg.resources = undefined));
      }
      const venue: Omit<Venue, 'propId' | 'listingId'> = {
        ...veneuData,
        slots: [],
        venueType: VenueType.TICKET_BOOKING
      } as Omit<Venue, 'propId' | 'listingId'>;
      if (venueId) {
        await dispatch(
          updateVenueThunk({
            listingId: listingId!,
            venueId,
            venue: _.omit(venue, 'isResourcesLinkedToVenue')
          })
        ).unwrap();
      } else {
        const { id } = await dispatch(addVenueThunk({ listingId: listingId!, venue })).unwrap();
        navigate(`${id}`);
      }
    }, 'Successfully Added Venue');
  };

  const commonProps = {
    register,
    errors,
    required: true,
    topLabel: true
  };

  return (
    <main className="font-medium p-8">
      <h1 className="text-xl font-bold mb-4">Venue</h1>
      <form onSubmit={handleSubmit(onSave)} className="flex items-start gap-4">
        <div className="w-4/5">
          <div className="grid grid-cols-2 gap-6 mb-4">
            <div className="space-y-2">
              <Input
                label="Workshop Phone Number"
                prefix="+91"
                type="tel"
                name="phoneNumber"
                {...commonProps}
              />
              <Input label="Venue Title" type="text" name="title" {...commonProps} />
            </div>
            <InputTextArea
              label="Venue Address"
              name="address"
              placeholder="Enter your venue address"
              className="min-h-16"
              {...commonProps}
            />
          </div>
          <div className="grid grid-cols-2 gap-6 mb-4">
            <InputSelect
              label="City"
              placeholder="Select City"
              name="city"
              options={cityOptions}
              {...commonProps}
            />
            <Input
              label="Venue Location"
              type="text"
              name="location"
              placeholder="Paste your venue's google map link"
              {...commonProps}
            />
          </div>
          <div className="w-fit">
            <InputSlider
              label="Is Ticket Count linked to venue?"
              name="isResourcesLinkedToVenue"
              suffix="(Note: This is not editable after venue is added)"
              readOnly={!_.isNil(venueId)}
              {...commonProps}
            />
          </div>
          <Packages
            listingId={listingId!}
            venueId={venueId}
            isResourcesLinkedToVenue={
              venueId ? venue?.isResourcesLinkedToVenue : watch('isResourcesLinkedToVenue')
            }
            {...{ register, errors, trigger, setValue, watch }}
          />
        </div>
        <Button
          className="bg-[#35e0a1] text-black px-8 py-2 mt-6 rounded sticky top-14"
          type="submit"
          label="Save Changes"
          isLoading={isThunkLoading(isLoading, addVenueThunk, updateVenueThunk)}
        />
      </form>
      {venue && venueId && (
        <div className="w-4/5">
          <Slots
            listingId={listingId!}
            venueId={venueId}
            slots={venue.slots}
            packages={venue.packages}
            isResourcesLinkedToVenue={venue.isResourcesLinkedToVenue}
          />
        </div>
      )}
    </main>
  );
};

export default AddVenue;
